import type {EChartsOption} from 'echarts';
import {TradeAllocationDto, TradeChainDto, TradeRecordDto, TradeRecordKind} from '../../../../models/generated-models';
import {ThaiDateTimePipe} from '../../../shared/commons/thai-datetime.pipe';
import {tradeRecordLabel} from '../trade.constants';
import {formatQuantity} from '../trade-utils';

/** Node/link colours, carried over from the map markers so both views read the same. */
export const JOURNEY_COLORS: Record<TradeRecordKind | 'CHECKPOINT', string> = {
  HARVEST: '#bbea6f',
  PURCHASE: '#feda14',
  SHIPMENT: '#7dd3fc',
  TRANSFORM: '#f97316',
  // The export checkpoint is not a record, so it gets a colour of its own.
  CHECKPOINT: '#e11d48'
};
export const JOURNEY_CURRENT_COLOR = '#04532b';
/** Border of a node that carries an observation (ข้อสังเกต). */
export const JOURNEY_WARNING_COLOR = '#f59e0b';

/**
 * masterdata.business_type ids that may legitimately start a chain even while they also buy in:
 * ไร่/สวน, สหกรณ์การเกษตร/สถาบันเกษตรกร, เกษตรกร, ฟาร์ม. Confirmed with the product owner; extend
 * here when ACFS names more.
 */
export const ORIGIN_BUSINESS_TYPE_IDS: readonly number[] = [1, 4, 10, 28];

/** One box of the Sankey: a record, or the ด่านส่งออก that closes an export chain. */
export interface JourneyNode {
  /** ECharts key — `T{recordId}` for records, `C{checkpointId}` for checkpoints. */
  name: string;
  recordId?: number;
  checkpoint: boolean;
  current: boolean;
  kind: TradeRecordKind | 'CHECKPOINT';
  title: string;
  subtitle: string;
  /** Total quantity on the record in kg. */
  quantity: number;
  /** What the layout uses: the quantity, floored so a 1 kg lot next to a 500 kg one stays visible. */
  value: number;
  remainingQty?: number;
  recordedAt?: Date;
  businessId?: number;
  businessTypeIds: number[];
  /** Origin lot carrying at least one certificate ThaiTraces (มกอช.) holds for its owner. */
  certified: boolean;
  /** Certificates on the lot that are only keyed in (not checkable against มกอช.). */
  selfKeyedCertificateNos: string[];
  /** Observations worth a second look (ข้อสังเกต); empty for an unremarkable node. */
  warnings: string[];
  itemStyle: {color: string; borderColor: string; borderWidth: number};
  /** An export record sits right next to its ด่าน box, so its label goes to the left of it. */
  label: {fontWeight: 'bold' | 'normal'; position?: 'left' | 'right'};
}

/** One ribbon: `quantity` kg drawn from `source` into `target` (`value` is the floored layout width). */
export interface JourneyLink {
  source: string;
  target: string;
  quantity: number;
  value: number;
}

/** Nothing on the graph is drawn thinner than this share of the biggest quantity. */
export const MIN_VISIBLE_SHARE = 0.03;

export interface JourneySankey {
  nodes: JourneyNode[];
  links: JourneyLink[];
}

const thaiDate = new ThaiDateTimePipe();

function recordKey(id: number): string {
  return `T${id}`;
}

function nodeTitle(record: TradeRecordDto): string {
  return record.businessTitle || record.farmName || tradeRecordLabel(record);
}

function nodeSubtitle(record: TradeRecordDto): string {
  const product = (record.productName ?? '').trim();
  return `${tradeRecordLabel(record)}${product ? ' · ' + product : ''} ${formatQuantity(record.quantity ?? 0)} กก.`;
}

const clean = (values: (string | undefined)[] | undefined): string[] =>
  (values ?? []).map((value) => `${value ?? ''}`.trim()).filter((value) => value !== '');

/** Certificates ThaiTraces holds for the owner; an older payload without the field falls back to all of them. */
function verifiedCertificateNos(record: TradeRecordDto): string[] {
  return record.verifiedCertificateNos === undefined ? clean(record.certificateNos) : clean(record.verifiedCertificateNos);
}

function selfKeyedCertificateNos(record: TradeRecordDto): string[] {
  const verified = new Set(verifiedCertificateNos(record));
  return clean(record.certificateNos).filter((no) => !verified.has(no));
}

function recordNode(record: TradeRecordDto, current: boolean): JourneyNode | null {
  if (record.id == null) {
    return null;
  }
  const kind: TradeRecordKind = record.kind ?? 'HARVEST';
  return {
    name: recordKey(record.id),
    recordId: record.id,
    checkpoint: false,
    current,
    kind,
    title: nodeTitle(record),
    subtitle: nodeSubtitle(record),
    quantity: Math.max(0, record.quantity ?? 0),
    value: Math.max(0, record.quantity ?? 0),
    remainingQty: record.remainingQty,
    recordedAt: record.recordedAt,
    businessId: record.businessId,
    businessTypeIds: (record.businessTypeIds ?? []).map((id) => Number(id)),
    certified: verifiedCertificateNos(record).length > 0,
    selfKeyedCertificateNos: selfKeyedCertificateNos(record),
    warnings: [],
    itemStyle: {
      color: JOURNEY_COLORS[kind] ?? JOURNEY_COLORS.PURCHASE,
      borderColor: current ? JOURNEY_CURRENT_COLOR : 'transparent',
      borderWidth: current ? 3 : 0
    },
    label: {fontWeight: current ? 'bold' : 'normal'}
  };
}

/** The ด่านส่งออก box an export record flows into — the last stop of the chain. */
function checkpointNode(record: TradeRecordDto): JourneyNode | null {
  if (record.export !== true || record.id == null) {
    return null;
  }
  const name = (record.exportCheckpointName ?? '').trim();
  if (!name) {
    return null;
  }
  return {
    name: `C${record.exportCheckpointId ?? 'r' + record.id}`,
    checkpoint: true,
    current: false,
    kind: 'CHECKPOINT',
    title: name,
    subtitle: 'ด่านที่ส่งออก · สิ้นสุดห่วงโซ่',
    quantity: Math.max(0, record.quantity ?? 0),
    value: Math.max(0, record.quantity ?? 0),
    businessTypeIds: [],
    certified: true,
    selfKeyedCertificateNos: [],
    warnings: [],
    itemStyle: {color: JOURNEY_COLORS.CHECKPOINT, borderColor: 'transparent', borderWidth: 0},
    label: {fontWeight: 'normal'}
  };
}

/**
 * Nodes and ribbons of the journey: every ancestor, the record, every descendant and every
 * related lot, joined by the allocation lines between them (a pooled sale merges ribbons, a split lot fans out).
 * Export records get one more ribbon into their ด่านส่งออก. Lines whose ends are not on the
 * graph are dropped, so the chart can never be handed a dangling edge.
 */
export function buildJourneySankey(chain: TradeChainDto | null | undefined, observations = false): JourneySankey {
  const current = chain?.record;
  if (!current || current.id == null) {
    return {nodes: [], links: []};
  }
  const nodes = new Map<string, JourneyNode>();
  const add = (node: JourneyNode | null): void => {
    if (node && !nodes.has(node.name)) {
      nodes.set(node.name, node);
    }
  };
  const records: TradeRecordDto[] = [
    ...(chain.ancestors ?? []),
    current,
    ...(chain.descendants ?? chain.children ?? []),
    // The other lots this journey was pooled with, so a merge shows from every record on it.
    ...(chain.related ?? [])
  ];
  for (const record of records) {
    add(recordNode(record, record.id === current.id));
  }

  const links: JourneyLink[] = [];
  const seen = new Set<string>();
  const lines: TradeAllocationDto[] = chain.links ?? [
    // Older payloads carry only the record's own lines and the ones out of it.
    ...(current.sources ?? []),
    ...(chain.outgoing ?? [])
  ];
  for (const line of lines) {
    if (line.sourceRecordId == null || line.recordId == null) {
      continue;
    }
    const source = recordKey(line.sourceRecordId);
    const target = recordKey(line.recordId);
    const key = `${source}>${target}`;
    if (!nodes.has(source) || !nodes.has(target) || source === target || seen.has(key)) {
      continue;
    }
    seen.add(key);
    const quantity = Math.max(0, line.quantity ?? 0);
    links.push({source, target, quantity, value: quantity});
  }

  for (const record of records) {
    const checkpoint = checkpointNode(record);
    if (!checkpoint || record.id == null) {
      continue;
    }
    add(checkpoint);
    const quantity = Math.max(0, record.quantity ?? 0);
    links.push({source: recordKey(record.id), target: checkpoint.name, quantity, value: quantity});
    const exporter = nodes.get(recordKey(record.id));
    if (exporter) {
      exporter.label = {...exporter.label, position: 'left'};
    }
  }
  const graph: JourneySankey = {nodes: [...nodes.values()], links};
  // The observations are for officers: business users get the plain journey.
  return floorForLayout(observations ? markObservations(graph) : graph);
}

/**
 * The optimistic model lets any business start a chain, so the graph is where oddities show:
 *  - an origin lot with no certificate มกอช. holds (anyone may key a certificate in freely, so
 *    only a ThaiTraces-synced one counts);
 *  - an origin lot started by a business that also buys in and is not a farm-type business
 *    (a ล้ง "harvesting" — the goods did not come from a farm on this journey);
 *  - a sale that pools such a lot together with goods bought from upstream.
 */
function markObservations(graph: JourneySankey): JourneySankey {
  const byName = new Map(graph.nodes.map((node) => [node.name, node]));
  const buyingBusinesses = new Set(
    graph.nodes
      .filter((node) => !node.checkpoint && node.kind !== 'HARVEST' && node.businessId != null)
      .map((node) => node.businessId as number)
  );
  const startedMidway = new Set<string>();
  for (const node of graph.nodes) {
    if (node.checkpoint || node.kind !== 'HARVEST') {
      continue;
    }
    if (!node.certified) {
      node.warnings.push(node.selfKeyedCertificateNos.length > 0
        ? `ล็อตต้นทางไม่มีใบรับรอง มกษ. จาก ThaiTraces มีแต่ใบที่กรอกเอง (${node.selfKeyedCertificateNos.join(', ')}) — อาจเป็นการสวมสิทธิ์`
        : 'ล็อตต้นทางไม่มีใบรับรอง มกษ. จาก ThaiTraces — อาจเป็นการสวมสิทธิ์');
    }
    const originCapable = node.businessTypeIds.some((id) => ORIGIN_BUSINESS_TYPE_IDS.includes(id));
    if (node.businessId != null && buyingBusinesses.has(node.businessId) && !originCapable) {
      node.warnings.push(`เริ่มต้นที่ ${node.title} ซึ่งรับซื้อจากที่อื่นด้วย — ไม่ได้มาจากต้นทางของสายนี้`);
      startedMidway.add(node.name);
    }
  }
  for (const node of graph.nodes) {
    const incoming = graph.links.filter((link) => link.target === node.name);
    const fromMidway = incoming.filter((link) => startedMidway.has(link.source));
    if (fromMidway.length > 0 && fromMidway.length < incoming.length) {
      const kg = fromMidway.reduce((sum, link) => sum + link.quantity, 0);
      node.warnings.push(`รวมของจากล็อตที่เริ่มต้นกลางสาย ${formatQuantity(kg)} กก. เข้ากับของที่ซื้อจากต้นทาง`);
    }
  }
  for (const node of graph.nodes) {
    if (node.warnings.length > 0 && !node.current) {
      node.itemStyle.borderColor = JOURNEY_WARNING_COLOR;
      node.itemStyle.borderWidth = 3;
    }
  }
  byName.clear();
  return graph;
}

/**
 * A Sankey scales widths by value, so a 1 kg lot beside a 500 kg one would vanish. The
 * layout value is floored at a small share of the biggest quantity; tooltips keep the real kg.
 */
function floorForLayout(graph: JourneySankey): JourneySankey {
  const biggest = Math.max(0, ...graph.nodes.map((node) => node.quantity), ...graph.links.map((link) => link.quantity));
  const floor = biggest * MIN_VISIBLE_SHARE;
  for (const node of graph.nodes) {
    node.value = Math.max(node.quantity, floor);
  }
  for (const link of graph.links) {
    link.value = Math.max(link.quantity, floor);
  }
  return graph;
}

function escapeHtml(value: string): string {
  return value.replace(/[&<>'"]/g, (character) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', "'": '&#39;', '"': '&quot;'
  })[character] ?? character);
}

function nodeTooltip(node: JourneyNode): string {
  const rows = [`<strong>${escapeHtml(node.title)}</strong>`, escapeHtml(node.subtitle)];
  if (node.current) {
    rows.push('รายการนี้');
  }
  if (!node.checkpoint && node.remainingQty != null) {
    rows.push(`คงเหลือ ${formatQuantity(node.remainingQty)} กก.`);
  }
  if (node.recordedAt) {
    rows.push(escapeHtml(`${thaiDate.transform(node.recordedAt)}`));
  }
  for (const warning of node.warnings) {
    rows.push(`<span style="color:#b45309">⚠ ${escapeHtml(warning)}</span>`);
  }
  if (!node.checkpoint && !node.current) {
    rows.push('<span style="color:#64748b">คลิกเพื่อเปิดรายการ</span>');
  }
  return rows.join('<br>');
}

function linkTooltip(link: JourneyLink, byName: Map<string, JourneyNode>): string {
  const from = byName.get(link.source)?.title ?? link.source;
  const to = byName.get(link.target)?.title ?? link.target;
  return `${escapeHtml(from)} → ${escapeHtml(to)}<br>ดึงไป <strong>${formatQuantity(link.quantity)} กก.</strong>`;
}

interface TooltipParams {
  dataType?: string;
  data?: JourneyNode | JourneyLink;
}

/** The ECharts option for a left-to-right journey Sankey of the given chain. */
export function journeySankeyOptions(chain: TradeChainDto | null | undefined, observations = false): EChartsOption {
  const {nodes, links} = buildJourneySankey(chain, observations);
  const byName = new Map(nodes.map((node) => [node.name, node]));
  return {
    animationDuration: 400,
    tooltip: {
      trigger: 'item',
      confine: true,
      formatter: (params: unknown) => {
        const {dataType, data} = (params ?? {}) as TooltipParams;
        if (dataType === 'edge' && data) {
          return linkTooltip(data as JourneyLink, byName);
        }
        return data ? nodeTooltip(data as JourneyNode) : '';
      }
    },
    series: [
      {
        type: 'sankey',
        orient: 'horizontal',
        nodeAlign: 'left',
        nodeWidth: 18,
        nodeGap: 18,
        left: 16,
        right: 210,
        // Clear of the floating view switch (top) and the legend (bottom).
        top: 64,
        bottom: 56,
        draggable: false,
        emphasis: {focus: 'adjacency'},
        lineStyle: {color: 'gradient', curveness: 0.5, opacity: 0.45},
        label: {
          position: 'right',
          fontSize: 12,
          lineHeight: 16,
          color: '#1e293b',
          formatter: (params: unknown) => {
            const node = (params as {data?: JourneyNode}).data;
            if (!node) {
              return '';
            }
            const flag = node.warnings.length > 0 ? '{warn|⚠} ' : '';
            return `${flag}{title|${node.title}}\n{sub|${node.subtitle}}`;
          },
          rich: {
            warn: {fontSize: 12, color: '#b45309', fontWeight: 'bold', lineHeight: 16},
            title: {fontSize: 12, fontWeight: 'bold', lineHeight: 16},
            sub: {fontSize: 11, color: '#64748b', lineHeight: 15}
          }
        },
        data: nodes,
        links
      }
    ]
  };
}
